import type { Rect } from "./rect";

export interface Point {
  x: number;
  y: number;
}

export function degreesToRadians(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

export function normalizeDegrees(degrees: number): number {
  const normalized = degrees % 360;
  return normalized < -180 ? normalized + 360 : normalized > 180 ? normalized - 360 : normalized;
}

export function rectCenter(rect: Rect): Point {
  return {
    x: rect.x + rect.width / 2,
    y: rect.y + rect.height / 2,
  };
}

/** Pivot for `bounds`, using transform coords stored relative to `referenceBounds`. */
export function rotationOriginForBounds(
  bounds: Rect,
  referenceBounds: Rect,
  transform: { x: number; y: number; rotation: number },
): Point {
  if (!Number.isFinite(transform.x) || !Number.isFinite(transform.y) || (transform.x === 0 && transform.y === 0)) {
    return rectCenter(bounds);
  }

  if (!Number.isFinite(transform.rotation)) {
    return rectCenter(bounds);
  }

  return {
    x: bounds.x + (transform.x - referenceBounds.x),
    y: bounds.y + (transform.y - referenceBounds.y),
  };
}

export function transformRotationOrigin(rect: Rect, transform: { x: number; y: number; rotation: number }): Point {
  return rotationOriginForBounds(rect, rect, transform);
}

export function hasFixedTransformRotationOrigin(transform: { x: number; y: number; rotation: number }): boolean {
  return Number.isFinite(transform.rotation)
    && Number.isFinite(transform.x)
    && Number.isFinite(transform.y)
    && (transform.x !== 0 || transform.y !== 0);
}

export function rotatePoint(point: Point, center: Point, degrees: number): Point {
  if (degrees === 0) {
    return { ...point };
  }

  const radians = degreesToRadians(degrees);
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  const dx = point.x - center.x;
  const dy = point.y - center.y;
  return {
    x: center.x + dx * cos - dy * sin,
    y: center.y + dx * sin + dy * cos,
  };
}

export function inverseRotatePoint(point: Point, center: Point, degrees: number): Point {
  return rotatePoint(point, center, -degrees);
}

export function inverseRotateVector(vector: Point, center: Point, degrees: number): Point {
  if (degrees === 0) {
    return { ...vector };
  }

  const rotated = inverseRotatePoint({ x: center.x + vector.x, y: center.y + vector.y }, center, degrees);
  return { x: rotated.x - center.x, y: rotated.y - center.y };
}

export function rectCorners(rect: Rect): Point[] {
  return [
    { x: rect.x, y: rect.y },
    { x: rect.x + rect.width, y: rect.y },
    { x: rect.x + rect.width, y: rect.y + rect.height },
    { x: rect.x, y: rect.y + rect.height },
  ];
}

export function rotatedRectCorners(rect: Rect, degrees: number): Point[] {
  const center = rectCenter(rect);
  return rectCorners(rect).map(point => rotatePoint(point, center, degrees));
}

export function transformedRectCorners(rect: Rect, transform: { x: number; y: number; rotation: number }): Point[] {
  const rotation = Number.isFinite(transform.rotation) ? transform.rotation : 0;
  const center = transformRotationOrigin(rect, transform);
  return rectCorners(rect).map(point => rotatePoint(point, center, rotation));
}

export function boundsFromPoints(points: Point[]): Rect {
  if (points.length === 0) {
    return { x: 0, y: 0, width: 0, height: 0 };
  }

  const left = Math.min(...points.map(point => point.x));
  const top = Math.min(...points.map(point => point.y));
  const right = Math.max(...points.map(point => point.x));
  const bottom = Math.max(...points.map(point => point.y));
  return { x: left, y: top, width: right - left, height: bottom - top };
}
